export const handleAiGenerate = async ({
  prompt,
  id,
  locale,
  currentWorkspace,
  setAiLoading,
  setFormData,
  setErrorMessage,
  setOpenSnackbar,
  setSeverity,
  fieldType
}) => {
  const defaultModelId = currentWorkspace?.data?.defaultAiModel?.text;

  if (!defaultModelId) {
    setErrorMessage("No default AI model set");
    setOpenSnackbar(true);
    setSeverity("error");
    return;
  }

  const model = currentWorkspace?.data?.aiModels?.find(m => m?.id === defaultModelId);

  if (!model) {
    setErrorMessage("Default AI model not found");
    setOpenSnackbar(true);
    setSeverity("error");
    return;
  }

  let selectedModel;
  let endpoint;

  switch (model?.model) {
    case "deepseek":
      selectedModel = "deepseek-chat";
      endpoint = process.env.REACT_APP_DEEPSEEK_ENDPOINT;
      break;
    case "qwen":
      selectedModel = "Qwen/Qwen2.5-VL-72B-Instruct-AWQ";
      endpoint = process.env.REACT_APP_QWEN_ENDPOINT;
      break;
    case "chatgpt":
      selectedModel = "gpt-4o";
      endpoint = process.env.REACT_APP_CHATGPT_ENDPOINT;
      break;
    case "gemini":
      selectedModel = "gemini-2.5-flash";
      endpoint = process.env.REACT_APP_GEMINI_ENDPOINT;
      break;
    default:
      setErrorMessage("Unsupported AI model");
      setOpenSnackbar(true);
      setSeverity("error");
      return;
  }

  const requestBody =
    model?.model === "chatgpt"
      ? {
        model: selectedModel,
        input: [{ role: "user", content: prompt }],
        stream: true,
      }
      : model?.model === "gemini"
        ? {
          model: selectedModel,
          // stream: true, // Don't add stream here, gemini api supports stream enabling in endpoint
          contents: [
            {
              role: "user",
              parts: [
                { text: prompt }
              ]
            },
          ],
        }
        : {
          model: selectedModel,
          messages: [{ role: "user", content: prompt }],
          stream: true,
        };



  setAiLoading((prev) => ({ ...prev, [`${id}-${locale}`]: true }));

  const headers = {
    'Content-Type': 'application/json',
    ...(model?.model === 'gemini'
      ? { 'x-goog-api-key': model?.apiKey }
      : { 'Authorization': `Bearer ${model?.apiKey}` }),
  };

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let accumulatedText = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n').filter(line => line.trim() !== '');

      for (let line of lines) {
        line = line.trim();
        if (!line.startsWith('data: ')) continue;

        const data = line.replace('data: ', '').trim();
        if (data === '[DONE]') break;

        try {
          const parsed = JSON.parse(data);

          // Gemini chunk
          const textChunk =
            parsed.candidates?.[0]?.content?.parts?.map(p => p.text).join('') ||
            // OpenAI-style chunk (Deepskeek, chatgpt, qwen)
            parsed.choices?.[0]?.delta?.content ||
            "";

          if (textChunk) accumulatedText += textChunk;

          // Update form data each iteration
          if (accumulatedText) {
            const value =
              fieldType === 'richText'
                ? [{ type: 'paragraph', children: [{ text: accumulatedText }] }]
                : accumulatedText;

            setFormData(prev => ({
              ...prev,
              [id]: {
                ...prev[id],
                [locale]: value,
              },
            }));
          }
        } catch (e) {
          console.error('Error parsing JSON:', e, 'line:', line);
        }
      }
    }
  } catch (error) {
    console.error("Error:", error);
    setErrorMessage("Error generating text");
    setOpenSnackbar(true);
    setSeverity("error");
  } finally {
    setAiLoading(prev => ({ ...prev, [`${id}-${locale}`]: false }));
  }

};
